import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

const TermsPage = () => {
  const sections = [
    {
      title: 'Nature of Service',
      content: `PhysicsNotebook.in is a mentorship-based educational platform that helps students understand physics through guided learning, conceptual clarity, and personalized mentorship.

We provide:
• Concept explanations, notes, and study materials
• Mentorship and guidance sessions (online or offline)
• AI-assisted learning tools and feedback
• Progress tracking and personalized learning recommendations

We are not an edtech institution or a school; we are a mentorship initiative focused on helping students learn better.`
    },
    {
      title: 'No Account Creation',
      content: `You are not required to create an account to access our website or mentorship services.

However, in the course of mentorship interactions, we may collect certain personal and educational information directly from you (such as your name, contact details, academic goals, and performance data) to provide personalized support.`
    },
    {
      title: 'Data Collection and Usage',
      content: `By interacting with PhysicsNotebook.in, you consent to the collection and use of your data as described below:
• We may collect information such as your name, email address, phone number, and academic background when you fill out forms, contact mentors, or participate in mentorship programs.
• This data may be used for:
  - Improving mentorship quality and training our systems
  - Personalizing study guidance and follow-ups
  - Internal analytics, research, and educational content improvement
• All data is securely stored in Pune, Maharashtra, India.`
    },
    {
      title: 'Data Retention and Deletion',
      content: `• We retain data only as long as necessary to fulfill the purposes outlined above.
• You may request deletion of your data at any time by contacting us at aditya@physicsnotebook.in.
• Upon verification, we will permanently delete your data within a reasonable timeframe (typically within 15 working days).`
    },
    {
      title: 'Acceptable Use',
      content: `You agree to use PhysicsNotebook.in only for lawful, educational, and mentorship-related purposes. You shall not:
• Submit false or misleading information
• Share offensive, abusive, or harmful material
• Interfere with the functionality or integrity of the website
• Use automated systems or bots to extract or misuse content

We reserve the right to restrict or deny access to anyone engaging in such prohibited conduct.`
    },
    {
      title: 'Intellectual Property',
      content: `All content on PhysicsNotebook.in—including text, illustrations, designs, notes, and videos—is the property of PhysicsNotebook.in or its content creators and mentors.

• You may view or download materials only for personal learning.
• You may not reproduce, distribute, modify, or republish any material without our prior written consent.`
    },
    {
      title: 'Disclaimer',
      content: `Our mentorship services and materials are provided on an "as is" and "as available" basis.

While we strive to ensure accuracy and helpfulness, PhysicsNotebook.in does not guarantee that every resource or recommendation will meet specific learning outcomes.

We are not liable for:
• Academic performance results
• Errors or inaccuracies in study materials
• Any losses or damages resulting from your reliance on our content or mentorship`
    },
    {
      title: 'Limitation of Liability',
      content: `To the fullest extent permitted by law, PhysicsNotebook.in and its team shall not be liable for any indirect, incidental, or consequential damages arising from:
• Use or inability to use our services
• Errors or omissions in our content
• Any actions taken based on mentorship advice

Your sole remedy is to discontinue use of the Platform.`
    },
    {
      title: 'Changes to Terms',
      content: `We may update these Terms periodically to reflect improvements or legal requirements.

Updated versions will be posted on this page with a revised date.

Your continued use of the Platform after such updates constitutes acceptance of the revised Terms.`
    },
    {
      title: 'Governing Law and Jurisdiction',
      content: `These Terms shall be governed by and interpreted in accordance with the laws of India.

Any disputes shall be subject to the exclusive jurisdiction of the courts in Pune, Maharashtra.`
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Terms and Conditions - PhysicsNotebook.in</title>
        <meta name="description" content="Read our Terms and Conditions to understand the rules and guidelines for using PhysicsNotebook.in's educational services and resources." />
      </Helmet>

      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="font-display text-4xl font-semibold text-gray-900 sm:text-5xl mb-4">
              Terms and Conditions
            </h1>
            <p className="text-lg text-gray-600 mb-4">
              Effective Date: November 2025<br />
              Last Updated: November 2025
            </p>
            <div className="max-w-3xl mx-auto">
              <p className="mt-4 text-gray-700 mb-6">
                Welcome to PhysicsNotebook.in ("we," "our," or "us").
                By accessing or using our website and mentorship services (collectively, the "Platform"), you agree to comply with and be bound by these Terms and Conditions ("Terms"). Please read them carefully before using our services.
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
                <p className="text-yellow-700 font-medium">
                  If you do not agree with any part of these Terms, please discontinue using the Platform.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm border border-gray-100">

            <div className="prose prose-lg max-w-none">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="mb-8"
                >
                  <h2 className="font-display text-2xl font-semibold text-gray-900 mb-4">{section.title}</h2>
                  <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </motion.div>
              ))}

              <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg mt-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Questions About These Terms?</h3>
                <p className="text-gray-600 mb-4">
                  If you have any questions or concerns about these Terms and Conditions, please don't hesitate to contact us.
                </p>
                <div className="space-y-2 text-sm text-gray-600">
                  <p><strong>Email:</strong> aditya@physicsnotebook.in</p>
                  <p><strong>WhatsApp:</strong> +91 9309242445</p>
                  <p><strong>Address:</strong> Pune, Maharashtra, India</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
